/**
 * «¿Este plano sirve para presupuestar?» (OPUS ci, idea 10): al abrir un plano, un veredicto y sus
 * razones, leídos del archivo con las reglas de su formato. $0: nada de IA.
 *
 *  - IFC: de cada pieza, si tiene la medida por la que se presupuesta (declarada o de su geometría), si dice su
 *    tipo, si es «genérica» (IfcBuildingElementProxy: no dice si es muro, viga o ventana), en qué piso
 *    está y de qué material es (`PlanoCapa.instancias[].sinTipo/sinNivel/sinMaterial`).
 *  - DWG/DXF: escala, capas con nombre (no todo en la capa 0), bloques, cotas y textos, vistas que no suman.
 *  - PDF: se lee mirando las láminas → las cantidades son estimadas.
 *
 * Los umbrales del veredicto NO viven aquí fijos: están en la calibración del motor (panel staff,
 * `umbrales.chequeo_*`) y llegan por `GET /api/v1/planos/parametros-chequeo`. Esto es el default del
 * seed (los del demo para dirección, PROVISIONALES) y el respaldo si la calibración no responde.
 */
import type { PlanoEscalaElegida, PlanoInventario } from './domain.js';
import { factorDeEscala } from './escalaDelPlano.js';

export interface ParametrosChequeoPlano {
  /** IFC: % mínimo de piezas «listas» (con su medida + tipo + no genérica + piso) para «Sirve bien». */
  chequeo_listos_bien_pct: number;
  /** IFC: % mínimo de piezas listas para «Sirve, con cuidado»; por debajo, «Casi no sirve». */
  chequeo_listos_cuidado_pct: number;
  /** IFC: desde qué % de piezas con el problema se dice en las razones. */
  chequeo_aviso_sin_cantidades_pct: number;
  chequeo_aviso_genericos_pct: number;
  chequeo_aviso_sin_tipo_pct: number;
  chequeo_aviso_sin_nivel_pct: number;
  chequeo_aviso_sin_material_pct: number;
  /** DWG/DXF: % mínimo del dibujo en capas con nombre (fuera de la capa 0) para darlo por bueno. */
  chequeo_dwg_capas_con_nombre_pct: number;
  /** «Revisar el dibujo» (diagnóstico DWG, `diagnosticoDelDibujo.ts`): umbrales PROVISIONALES, a validar con la vara. */
  diag_duplicado_tol_m: number;
  diag_tol_relativa: number;
  diag_repetido_capa_pct: number;
  diag_nucleo_pct: number;
  diag_suelta_factor: number;
  diag_trazo_largo_factor: number;
  diag_caja_inflada_ratio: number;
  diag_bloque_escala_decadas: number;
  diag_bloque_vs_vista_pct: number;
  diag_gemelo_tol_rel: number;
  diag_gemelo_min_trazos: number;
  diag_cotas_no_cuadran_min: number;
  diag_cotas_no_cuadran_pct: number;
  diag_bloque_huella_max_trazos: number;
  diag_vista_min_entidades: number;
  /** «Revisar el modelo» (diagnóstico IFC, `diagnosticoDelModelo.ts`): PROVISIONALES, a validar con la vara. */
  diag_ifc_duplicado_tol_m: number;
  diag_ifc_duplicado_tol_rel: number;
  diag_ifc_tipo_min_piezas: number;
  diag_ifc_atipica_decadas: number;
  diag_ifc_atipica_capa_pct: number;
  diag_ifc_medida_cero_m: number;
  diag_ifc_nucleo_pct: number;
  diag_ifc_suelta_factor: number;
  diag_ifc_min_piezas: number;
}

export const PARAMETROS_CHEQUEO_PLANO_DEFAULT: Readonly<ParametrosChequeoPlano> = {
  diag_duplicado_tol_m: 0.001,
  diag_tol_relativa: 0.000001,
  diag_repetido_capa_pct: 2,
  diag_nucleo_pct: 2,
  diag_suelta_factor: 0.5,
  diag_trazo_largo_factor: 1.5,
  diag_caja_inflada_ratio: 4,
  diag_bloque_escala_decadas: 0.9,
  diag_bloque_vs_vista_pct: 50,
  diag_gemelo_tol_rel: 0.01,
  diag_gemelo_min_trazos: 4,
  diag_cotas_no_cuadran_min: 3,
  diag_cotas_no_cuadran_pct: 10,
  diag_bloque_huella_max_trazos: 2000,
  diag_vista_min_entidades: 20,
  diag_ifc_duplicado_tol_m: 0.01,
  diag_ifc_duplicado_tol_rel: 0.02,
  diag_ifc_tipo_min_piezas: 5,
  diag_ifc_atipica_decadas: 0.9,
  diag_ifc_atipica_capa_pct: 10,
  diag_ifc_medida_cero_m: 0.001,
  diag_ifc_nucleo_pct: 2,
  diag_ifc_suelta_factor: 1,
  diag_ifc_min_piezas: 20,
  chequeo_listos_bien_pct: 85,
  chequeo_listos_cuidado_pct: 50,
  chequeo_aviso_sin_cantidades_pct: 10,
  chequeo_aviso_genericos_pct: 5,
  chequeo_aviso_sin_tipo_pct: 10,
  chequeo_aviso_sin_nivel_pct: 1,
  chequeo_aviso_sin_material_pct: 10,
  chequeo_dwg_capas_con_nombre_pct: 80,
};

export const RANGO_PARAMETROS_CHEQUEO_PLANO: Readonly<Record<keyof ParametrosChequeoPlano, readonly [number, number]>> = {
  chequeo_listos_bien_pct: [1, 100],
  chequeo_listos_cuidado_pct: [0, 100],
  chequeo_aviso_sin_cantidades_pct: [0, 100],
  chequeo_aviso_genericos_pct: [0, 100],
  chequeo_aviso_sin_tipo_pct: [0, 100],
  chequeo_aviso_sin_nivel_pct: [0, 100],
  chequeo_aviso_sin_material_pct: [0, 100],
  chequeo_dwg_capas_con_nombre_pct: [0, 100],
  diag_duplicado_tol_m: [0.0001, 0.05],
  diag_tol_relativa: [0, 0.001],
  diag_repetido_capa_pct: [0, 100],
  diag_nucleo_pct: [0, 20],
  diag_suelta_factor: [0.05, 20],
  diag_trazo_largo_factor: [1, 50],
  diag_caja_inflada_ratio: [1, 1000],
  diag_bloque_escala_decadas: [0.1, 4],
  diag_bloque_vs_vista_pct: [1, 100],
  diag_gemelo_tol_rel: [0.0001, 0.1],
  diag_gemelo_min_trazos: [1, 1000],
  diag_cotas_no_cuadran_min: [1, 1000],
  diag_cotas_no_cuadran_pct: [0, 100],
  diag_bloque_huella_max_trazos: [10, 100000],
  diag_vista_min_entidades: [2, 100000],
  diag_ifc_duplicado_tol_m: [0.001, 0.5],
  diag_ifc_duplicado_tol_rel: [0, 0.5],
  diag_ifc_tipo_min_piezas: [3, 10000],
  diag_ifc_atipica_decadas: [0.1, 4],
  diag_ifc_atipica_capa_pct: [0, 100],
  diag_ifc_medida_cero_m: [0, 0.1],
  diag_ifc_nucleo_pct: [0, 20],
  diag_ifc_suelta_factor: [0.1, 50],
  diag_ifc_min_piezas: [2, 100000],
};

/** Etiqueta y pista de cada parámetro para el panel de Calibración (el panel no escribe textos propios). */
export const DESCRIPCION_PARAMETROS_CHEQUEO_PLANO: Readonly<Record<keyof ParametrosChequeoPlano, { etiqueta: string; pista: string }>> = {
  chequeo_listos_bien_pct: { etiqueta: 'IFC «Sirve bien»: piezas listas desde (%)', pista: 'Lista = tiene su medida, tipo y piso, y no es genérica' },
  chequeo_listos_cuidado_pct: { etiqueta: 'IFC «Sirve, con cuidado»: piezas listas desde (%)', pista: 'Por debajo: «Casi no sirve»' },
  chequeo_aviso_sin_cantidades_pct: { etiqueta: 'IFC: avisar piezas sin su medida desde (%)', pista: 'Ni el modelo la dice ni se pudo medir de su forma' },
  chequeo_aviso_genericos_pct: { etiqueta: 'IFC: avisar piezas genéricas desde (%)', pista: 'No dicen si son muro, viga o ventana' },
  chequeo_aviso_sin_tipo_pct: { etiqueta: 'IFC: avisar piezas sin tipo desde (%)', pista: 'Se agrupan por su nombre o su clase' },
  chequeo_aviso_sin_nivel_pct: { etiqueta: 'IFC: avisar piezas sin piso desde (%)', pista: 'El modelo no dice en qué piso están' },
  chequeo_aviso_sin_material_pct: { etiqueta: 'IFC: avisar piezas sin material desde (%)', pista: 'Sin material no se pueden pesar' },
  chequeo_dwg_capas_con_nombre_pct: { etiqueta: 'DWG: dibujo en capas con nombre desde (%)', pista: 'Lo demás está en la capa 0' },
  diag_duplicado_tol_m: { etiqueta: 'DWG: dos piezas están «encima» si se separan menos de (m)', pista: 'Revisar el dibujo: piezas y trazos repetidos' },
  diag_tol_relativa: { etiqueta: 'DWG sin unidad: misma tolerancia, como fracción del tamaño del dibujo', pista: 'Solo cuando no se sabe la unidad' },
  diag_repetido_capa_pct: { etiqueta: 'DWG: avisar fuerte si lo repetido es al menos este % del largo de la capa', pista: 'Por debajo, el aviso es menor' },
  diag_nucleo_pct: { etiqueta: 'DWG: recorte por lado para el centro de cada vista (%)', pista: 'Se ignora este % de piezas extremas para saber dónde está el plano' },
  diag_suelta_factor: { etiqueta: 'DWG: pieza suelta si está más lejos que esto × el lado mayor de su vista', pista: 'Relativo al propio dibujo, no en metros' },
  diag_trazo_largo_factor: { etiqueta: 'DWG: trazo desmedido si es más largo que esto × la diagonal de su vista', pista: 'Relativo al propio dibujo' },
  diag_caja_inflada_ratio: { etiqueta: 'DWG: la vista se agrandó por sueltas si su caja es esto × su centro', pista: 'Razón de áreas' },
  diag_bloque_escala_decadas: { etiqueta: 'DWG: bloque a otra escala si difiere de sus iguales esta cantidad de potencias de 10', pista: '0.9 ≈ 8 veces; las ventanas estiradas a propósito no saltan' },
  diag_bloque_vs_vista_pct: { etiqueta: 'DWG: pieza desmedida si mide más que este % del lado menor de su vista', pista: 'Un bloque en otra unidad suele agrandar la vista' },
  diag_gemelo_tol_rel: { etiqueta: 'DWG: tolerancia para «misma forma» de dos bloques (fracción de su tamaño)', pista: 'Para detectar el mismo dibujo con dos nombres' },
  diag_gemelo_min_trazos: { etiqueta: 'DWG: trazos mínimos para comparar dos bloques', pista: 'Un círculo solo no cuenta' },
  diag_cotas_no_cuadran_min: { etiqueta: 'DWG: avisar cotas que no cuadran desde (n)', pista: 'Número mínimo de cotas' },
  diag_cotas_no_cuadran_pct: { etiqueta: 'DWG: … y desde este % de las cotas que miden un trazo', pista: 'Se avisa si se cumplen las dos' },
  diag_bloque_huella_max_trazos: { etiqueta: 'DWG: no comparar bloques con más trazos que esto', pista: 'Rendimiento' },
  diag_vista_min_entidades: { etiqueta: 'DWG: una vista se revisa si tiene al menos (n) piezas', pista: 'Con menos, no hay un «centro» confiable' },
  diag_ifc_duplicado_tol_m: { etiqueta: 'IFC: dos piezas están «encima» si sus centros se separan menos de (m)', pista: 'Revisar el modelo: piezas repetidas' },
  diag_ifc_duplicado_tol_rel: { etiqueta: 'IFC: … y sus medidas difieren menos de esta fracción', pista: 'Se comparan largo, ancho y alto' },
  diag_ifc_tipo_min_piezas: { etiqueta: 'IFC: un tipo se compara consigo mismo si tiene al menos (n) piezas', pista: 'Con menos no hay «lo normal» del tipo' },
  diag_ifc_atipica_decadas: { etiqueta: 'IFC: pieza desmedida si difiere de su tipo esta cantidad de potencias de 10', pista: '0.9 ≈ 8 veces' },
  diag_ifc_atipica_capa_pct: { etiqueta: 'IFC: avisar fuerte si lo desmedido pesa al menos este % de su tipo', pista: 'Por debajo, el aviso es menor' },
  diag_ifc_medida_cero_m: { etiqueta: 'IFC: una medida de la pieza cuenta como cero por debajo de (m)', pista: 'Piezas aplastadas' },
  diag_ifc_nucleo_pct: { etiqueta: 'IFC: recorte por lado para el centro de la obra (%)', pista: 'Se ignora este % de piezas extremas' },
  diag_ifc_suelta_factor: { etiqueta: 'IFC: pieza lejos si está más lejos que esto × el lado mayor de la obra', pista: 'Relativo al propio modelo' },
  diag_ifc_min_piezas: { etiqueta: 'IFC: buscar piezas lejos solo con al menos (n) piezas', pista: 'Con menos no hay un centro confiable' },
};

/** Completa y sanea lo que venga (calibración, API): clave ausente o fuera de rango → default. Nunca tira. */
export function normalizarParametrosChequeoPlano(x: unknown): ParametrosChequeoPlano {
  const src = (x && typeof x === 'object' ? x : {}) as Record<string, unknown>;
  const out = { ...PARAMETROS_CHEQUEO_PLANO_DEFAULT };
  for (const k of Object.keys(out) as (keyof ParametrosChequeoPlano)[]) {
    const v = src[k];
    const [mn, mx] = RANGO_PARAMETROS_CHEQUEO_PLANO[k];
    if (typeof v === 'number' && Number.isFinite(v) && v >= mn && v <= mx) out[k] = v;
  }
  return out;
}

export type VeredictoChequeo = 'bien' | 'cuidado' | 'no';

export const TITULO_VEREDICTO_CHEQUEO: Readonly<Record<VeredictoChequeo, string>> = {
  bien: 'Sirve bien para presupuestar',
  cuidado: 'Sirve, con cuidado',
  no: 'Casi no sirve para presupuestar',
};

/** Un renglón del chequeo: 'ok' = a favor, 'mal' = en contra, 'info' = para saber. */
export interface PuntoDelChequeo {
  tono: 'ok' | 'mal' | 'info';
  texto: string;
}

export interface IndicadorDelChequeo {
  etiqueta: string;
  /** 0–100. */
  pct: number;
  /** Piezas con el problema (ausente en «con sus cantidades», que es a favor). */
  cuenta?: number;
  /** true = más es mejor. */
  bueno?: boolean;
}

export interface ChequeoDelPlano {
  veredicto: VeredictoChequeo;
  /** Frase debajo del veredicto (IFC: cuánto se saca directo del archivo). */
  resumen: string | null;
  indicadores: IndicadorDelChequeo[];
  puntos: PuntoDelChequeo[];
  /** Qué se revisó, en una línea. */
  nota: string;
}

const pct = (n: number, d: number) => (d > 0 ? (n / d) * 100 : 0);

/** Una pieza IFC y la medida por la que se presupuesta su tipo (`PlanoCapa.medidas.manda`). */
type InstanciaIfc = NonNullable<PlanoInventario['capas'][number]['instancias']>[number];
type MandaIfc = NonNullable<PlanoInventario['capas'][number]['medidas']>['manda'];

/**
 * ¿La pieza tiene la medida por la que se presupuesta su tipo? Con `manda`, esa magnitud > 0 (por conteo, siempre); sin ella, alguna
 * de área, longitud o volumen. UNA sola regla: la usan el chequeo del modelo y «Revisar el modelo».
 */
export function tieneSuMedida(i: InstanciaIfc, manda: MandaIfc | undefined): boolean {
  const valor = (m: 'area' | 'longitud' | 'volumen') => (i[m] ?? 0) > 0;
  return manda === 'conteo' || (manda ? valor(manda) : valor('area') || valor('longitud') || valor('volumen'));
}
const fmt = (n: number) => n.toLocaleString('es-MX');
const piezas = (n: number) => (n === 1 ? '1 pieza' : `${fmt(n)} piezas`);

export const CLASE_IFC_GENERICA = 'IfcBuildingElementProxy';

/** IFC: el chequeo sobre las piezas del inventario. null si el inventario no trae piezas. */
export function chequearModeloIfc(
  inv: PlanoInventario,
  p: ParametrosChequeoPlano,
  /** «Revisar el modelo»: con él, los avisos dicen CUÁLES piezas (con su lista) y los puntos de porcentaje no se repiten. */
  diagnostico?: { avisos: readonly { gravedad: 'alta' | 'media' | 'info' }[] } | null,
): ChequeoDelPlano | null {
  let n = 0;
  let conCantidades = 0;
  let deGeometria = 0;
  let sinTipo = 0;
  let genericos = 0;
  let sinNivel = 0;
  let sinMaterial = 0;
  let listos = 0;
  for (const c of inv.capas) {
    if (c.categoria == null) continue;
    const generica = c.categoria === CLASE_IFC_GENERICA;
    // «Tiene su medida» = la pieza tiene la medida por la que se presupuesta su tipo (la que manda), la
    // declare el modelo o se mida de su geometría (validado contra IfcOpenShell). Las medidas de la
    // geometría se cuentan aparte, como dato: no frenan.
    const manda = c.medidas?.manda;
    for (const i of c.instancias ?? []) {
      n++;
      const estimadas = i.magnitudesEstimadas ?? [];
      const cantidades = tieneSuMedida(i, manda);
      if (cantidades && manda && manda !== 'conteo' && estimadas.includes(manda)) deGeometria++;
      if (cantidades) conCantidades++;
      if (i.sinTipo) sinTipo++;
      if (generica) genericos++;
      if (i.sinNivel) sinNivel++;
      if (i.sinMaterial) sinMaterial++;
      if (cantidades && !i.sinTipo && !generica && !i.sinNivel) listos++;
    }
  }
  if (n === 0) return null;
  const pl = pct(listos, n);
  const veredicto: VeredictoChequeo =
    pl >= p.chequeo_listos_bien_pct ? 'bien' : pl >= p.chequeo_listos_cuidado_pct ? 'cuidado' : 'no';
  const puntos: PuntoDelChequeo[] = [];
  const sinCant = n - conCantidades;
  if (!diagnostico && pct(sinCant, n) > p.chequeo_aviso_sin_cantidades_pct)
    puntos.push({ tono: 'mal', texto: `${piezas(sinCant)} no ${sinCant === 1 ? 'tiene' : 'tienen'} la medida por la que se presupuesta: ni el modelo la dice ni se pudo medir de su forma. Habría que medirlas a mano.` });
  if (!diagnostico && pct(genericos, n) > p.chequeo_aviso_genericos_pct)
    puntos.push({ tono: 'mal', texto: `${piezas(genericos)} ${genericos === 1 ? 'viene' : 'vienen'} como «genéricas»: el modelo no dice si son muro, viga o ventana. Están en «Revisar: genéricos».` });
  if (!diagnostico && pct(sinTipo, n) > p.chequeo_aviso_sin_tipo_pct)
    puntos.push({ tono: 'mal', texto: `${piezas(sinTipo)} no ${sinTipo === 1 ? 'dice' : 'dicen'} de qué tipo ${sinTipo === 1 ? 'es' : 'son'}: se agruparon por su nombre o su clase; revisa que estén juntas las que van juntas.` });
  if (!diagnostico && pct(sinNivel, n) > p.chequeo_aviso_sin_nivel_pct)
    puntos.push({ tono: 'mal', texto: `${piezas(sinNivel)} no ${sinNivel === 1 ? 'dice' : 'dicen'} en qué piso ${sinNivel === 1 ? 'está' : 'están'}.` });
  if (!diagnostico && pct(sinMaterial, n) > p.chequeo_aviso_sin_material_pct)
    puntos.push({ tono: 'mal', texto: `${piezas(sinMaterial)} no ${sinMaterial === 1 ? 'dice' : 'dicen'} de qué material ${sinMaterial === 1 ? 'es' : 'son'}: no se pueden pesar.` });
  let veredictoFinal = veredicto;
  const graves = diagnostico?.avisos.filter((a) => a.gravedad === 'alta').length ?? 0;
  if (graves > 0) {
    if (veredictoFinal === 'bien') veredictoFinal = 'cuidado';
    puntos.push({ tono: 'mal', texto: `Encontramos ${graves === 1 ? '1 cosa del modelo que cambia' : `${fmt(graves)} cosas del modelo que cambian`} cantidades: mira «Revisar el modelo».` });
  }
  if (diagnostico ? !diagnostico.avisos.some((a) => a.gravedad !== 'info') : !puntos.length) puntos.unshift({ tono: 'ok', texto: 'No encontramos nada que te vaya a frenar.' });
  if (deGeometria > 0)
    puntos.push({
      tono: 'info',
      texto: `${piezas(deGeometria)}: el modelo no dice su medida y se midió de su forma en 3D. En la lista salen marcadas como estimadas.`,
    });
  return {
    veredicto: veredictoFinal,
    resumen: `Con este modelo puedes sacar ${Math.round(pl)} % de las piezas directo del archivo. Lo demás conviene revisarlo a mano.`,
    indicadores: [
      { etiqueta: 'Piezas con su medida', pct: pct(conCantidades, n), bueno: true },
      { etiqueta: 'Piezas sin tipo', pct: pct(sinTipo, n), cuenta: sinTipo },
      { etiqueta: 'Piezas genéricas', pct: pct(genericos, n), cuenta: genericos },
      { etiqueta: 'Piezas sin piso', pct: pct(sinNivel, n), cuenta: sinNivel },
      { etiqueta: 'Piezas sin material', pct: pct(sinMaterial, n), cuenta: sinMaterial },
    ],
    puntos,
    nota: `Revisamos ${piezas(n)} con las reglas del formato IFC.`,
  };
}

/** DWG/DXF: escala, capas, bloques, cotas y vistas del inventario del lector. */
export function chequearPlanoDwg(
  inv: PlanoInventario,
  p: ParametrosChequeoPlano,
  escalaElegida?: PlanoEscalaElegida | null,
  diagnostico?: { avisos: readonly { gravedad: 'alta' | 'media' | 'info' }[] } | null,
): ChequeoDelPlano {
  const esc = factorDeEscala(inv, escalaElegida);
  const escalaOk = !esc.dudosa && esc.factor != null;
  const unidad = esc.fuente === 'usuario' ? escalaElegida?.unidad ?? null : inv.escala?.unidad ?? inv.unidades?.nombre ?? null;
  const entidades = inv.capas.reduce((s, c) => s + c.n_entidades, 0);
  const enCapa0 = inv.capas.filter((c) => c.capa === '0').reduce((s, c) => s + c.n_entidades, 0);
  const pctCapas = pct(entidades - enCapa0, entidades);
  const capasOk = entidades > 0 && pctCapas >= p.chequeo_dwg_capas_con_nombre_pct;
  const bloques = inv.bloques.reduce((s, b) => s + (b.n ?? 0), 0);
  const cotas = inv.anotaciones?.cotas.n ?? 0;
  const textos = inv.anotaciones?.textos.length ?? 0;

  const puntos: PuntoDelChequeo[] = [
    {
      tono: escalaOk ? 'ok' : 'mal',
      texto: escalaOk
        ? esc.fuente === 'usuario'
          ? `Escala: la elegiste tú${unidad ? ` (${unidad})` : ''}.`
          : `Se sabe su escala${unidad ? `: está en ${unidad}` : ''}.`
        : 'No se sabe con seguridad en qué unidad está: una medida podría salir 100 o 1,000 veces más grande o más chica. Te preguntamos la unidad antes de medir.',
    },
    {
      tono: capasOk ? 'ok' : 'mal',
      texto:
        entidades > 0 && enCapa0 < entidades
          ? `${Math.round(pctCapas)} % del dibujo está en capas con nombre: se puede saber qué es muro, qué es puerta y qué es cota.`
          : 'Todo está en la capa 0: no se puede saber qué línea es muro, cuál es puerta y cuál es cota.',
    },
    {
      tono: bloques > 0 ? 'ok' : 'mal',
      texto:
        bloques > 0
          ? `${fmt(bloques)} ${bloques === 1 ? 'pieza viene como bloque' : 'piezas vienen como bloques'} (puertas, ventanas, muebles): se cuentan solas.`
          : 'No hay bloques: las puertas y ventanas habría que contarlas a mano.',
    },
    {
      tono: cotas > 0 || textos > 0 ? 'ok' : 'mal',
      texto:
        cotas > 0 || textos > 0
          ? `${fmt(cotas)} ${cotas === 1 ? 'cota' : 'cotas'} y ${fmt(textos)} ${textos === 1 ? 'texto' : 'textos'}: sirven para revisar medidas y saber qué es cada espacio.`
          : 'No trae cotas ni textos.',
    },
  ];
  const buenos = puntos.filter((x) => x.tono === 'ok').length;
  let veredicto: VeredictoChequeo = !escalaOk ? (buenos >= 2 ? 'cuidado' : 'no') : buenos === puntos.length ? 'bien' : 'cuidado';
  // «Revisar el dibujo»: un aviso grave (cambia cantidades) baja «bien» a «cuidado» y lo resume en un punto.
  const graves = diagnostico?.avisos.filter((a) => a.gravedad === 'alta').length ?? 0;
  if (graves > 0) {
    if (veredicto === 'bien') veredicto = 'cuidado';
    puntos.push({
      tono: 'mal',
      texto: `Encontramos ${graves === 1 ? '1 cosa del dibujo que cambia' : `${fmt(graves)} cosas del dibujo que cambian`} cantidades: mira «Revisar el dibujo».`,
    });
  }

  const noSuman = (inv.vistas ?? []).filter((v) => !v.suma).length;
  if (noSuman > 0)
    puntos.push({
      tono: 'info',
      texto: `${noSuman === 1 ? '1 vista no suma' : `${noSuman} vistas no suman`} (fachada, corte o detalle): lo que está dibujado ahí no se cuenta dos veces.`,
    });
  const ocultas = inv.capas_ocultas?.length ?? 0;
  if (ocultas > 0)
    puntos.push({
      tono: 'info',
      texto: `${ocultas === 1 ? '1 capa está apagada o congelada' : `${ocultas} capas están apagadas o congeladas`}: no se ${ocultas === 1 ? 've' : 'ven'} en el CAD y no se midieron.`,
    });
  return { veredicto, resumen: null, indicadores: [], puntos, nota: `Revisamos ${fmt(entidades)} elementos del dibujo.` };
}

/** PDF leído por visión: no hay geometría, las cantidades son estimadas. */
export function chequearPlanoPdf(inv: PlanoInventario | null): ChequeoDelPlano {
  const n = inv?.cantidadesEstimadas?.length ?? 0;
  return {
    veredicto: n > 0 ? 'cuidado' : 'no',
    resumen: null,
    indicadores: [],
    puntos: [
      { tono: 'mal', texto: 'Es un PDF: no trae geometría que se pueda medir. Se leyeron sus láminas y las cantidades son estimadas.' },
      n > 0
        ? { tono: 'ok', texto: `Se ${n === 1 ? 'leyó 1 cantidad' : `leyeron ${fmt(n)} cantidades`} de las láminas.` }
        : { tono: 'mal', texto: 'No se pudo leer ninguna cantidad de las láminas.' },
      { tono: 'info', texto: 'Si tienes el DWG o el IFC de este plano, súbelo: sus cantidades se miden del dibujo.' },
    ],
    nota: 'Revisamos cómo se leyó el PDF.',
  };
}
